import type { StyleMap } from "../blocks/shared-block-styles";
import type { JSONContent } from "@tiptap/core";
import type { Editor } from "@tiptap/core";
import { NodeSelection, type Selection, TextSelection } from "@tiptap/pm/state";

/**
 * Ready-made sections for the `/` menu.
 *
 * Every one of these is plain row/cell/block JSON — the same shapes a pasted
 * design parses into — so the canvas, the send renderer and the HTML parser
 * already know how to draw and reproduce them. Nothing here adds a node type.
 */

/** Padding presets offered for a section, smallest first. */
export const SECTION_PADDING_OPTIONS = [
  { label: "None", value: "" },
  { label: "S", value: "16px" },
  { label: "M", value: "32px" },
  { label: "L", value: "48px" },
] as const;

const WHITE_TEXT: StyleMap = { color: "#ffffff" };

function cell(content: JSONContent[], attrs: Record<string, unknown> = {}) {
  return { type: "emailCell", attrs: { colSpan: 4, ...attrs }, content };
}

function row(cells: JSONContent[]): JSONContent {
  return { type: "emailRow", content: cells };
}

function paragraph(text = "", attrs: Record<string, unknown> = {}) {
  return {
    type: "emailParagraph",
    attrs,
    ...(text ? { content: [{ type: "text", text }] } : {}),
  };
}

function heading(text: string, attrs: Record<string, unknown> = {}) {
  return {
    type: "emailHeading",
    attrs: { level: 2, ...attrs },
    content: [{ type: "text", text }],
  };
}

const emptyImage = (): JSONContent => ({ type: "emailImage" });

/**
 * The style map that paints an image behind a cell's contents.
 *
 * `background` is also kept as an attribute, which is how older Outlook builds
 * and some webmail clients read a cell's background image.
 */
export function backgroundImageStyle(src: string): {
  style: StyleMap;
  attributes: Record<string, string>;
} {
  return {
    style: {
      "background-image": `url('${src}')`,
      "background-size": "cover",
      "background-position": "center",
      "background-repeat": "no-repeat",
    },
    attributes: { background: src },
  };
}

/** The image a cell is painted with, read back out of its style map. */
export function readBackgroundImage(attrs: Record<string, unknown>): string {
  const style = (attrs.style ?? {}) as StyleMap;
  const match = /url\(\s*['"]?([^'")]+)['"]?\s*\)/.exec(
    style["background-image"] ?? "",
  );
  return match?.[1] ?? "";
}

export const layoutPresets = {
  /** A coloured band with room to write. */
  section: () =>
    row([
      cell(
        [
          heading("Section heading", { textAlign: "center" }),
          paragraph("Add your text here.", { textAlign: "center" }),
        ],
        { backgroundColor: "#f3f4f6", padding: "32px" },
      ),
    ]),

  /** Text laid over a photo — a hero banner. */
  backgroundImage: (src: string) => {
    const { style, attributes } = backgroundImageStyle(src);
    return row([
      cell(
        [
          heading("Your headline", { textAlign: "center", style: WHITE_TEXT }),
          paragraph("Write a short line about the offer.", {
            textAlign: "center",
            style: WHITE_TEXT,
          }),
          { type: "emailButton", attrs: { text: "Book now" } },
        ],
        // The colour shows while the image loads, and in clients that block
        // images — so the white text stays readable either way.
        { backgroundColor: "#1f2937", padding: "48px 32px", style, attributes },
      ),
    ]);
  },

  twoColumns: () =>
    row([
      cell([paragraph("Left column")], { colSpan: 2 }),
      cell([paragraph("Right column")], { colSpan: 2 }),
    ]),

  // The four-column grid can't split evenly in three, so each cell carries its
  // true share as a width; the spans only matter if someone drags a border.
  threeColumns: () =>
    row([
      cell([paragraph("Column one")], { colSpan: 1, width: "33.33%" }),
      cell([paragraph("Column two")], { colSpan: 1, width: "33.33%" }),
      cell([paragraph("Column three")], { colSpan: 2, width: "33.34%" }),
    ]),

  imageText: () =>
    row([
      cell([emptyImage()], { colSpan: 2 }),
      cell([heading("Heading", { level: 3 }), paragraph("Describe it here.")], {
        colSpan: 2,
      }),
    ]),

  /** A row holding one image — an empty "click to upload" one by default. */
  image: (image: JSONContent = emptyImage()) => row([cell([image])]),

  divider: () => row([cell([{ type: "emailDivider" }], { padding: "16px 0" })]),
};

/** True for the row a fresh line starts as: one cell holding one empty paragraph. */
function isBlankRow(node: import("@tiptap/pm/model").Node): boolean {
  const onlyCell = node.childCount === 1 ? node.firstChild : null;
  const onlyBlock = onlyCell?.childCount === 1 ? onlyCell.firstChild : null;
  return (
    onlyBlock?.type.name === "emailParagraph" && onlyBlock.content.size === 0
  );
}

/**
 * The top-level row the selection is in. A click in block mode selects a whole
 * row (a node selection at the document's top level), which counts as that row.
 */
function selectedRow(selection: Selection) {
  if (selection instanceof NodeSelection && selection.$from.depth === 0) {
    return { node: selection.node, start: selection.from };
  }
  const { $from } = selection;
  if ($from.depth < 1) return null;
  return { node: $from.node(1), start: $from.before(1) };
}

/**
 * Put a ready-made section into the document at the cursor's row.
 *
 * A blank row (the line the `/` was typed on) is replaced; otherwise the
 * section goes below the current row.
 *
 * A section with text gets that text selected, so typing replaces the
 * placeholder. One without (an image, a divider) leaves the cursor on an empty
 * line straight below it — added unless one is already there — so the next
 * image stacks right under it even with a signature further down.
 */
export function insertLayoutRow(editor: Editor, content: JSONContent) {
  const { state } = editor;
  const current = selectedRow(state.selection);
  const node = state.schema.nodeFromJSON(content);
  const blankRow = () => state.schema.nodeFromJSON(row([cell([paragraph()])]));

  const tr = state.tr;
  let insertedAt: number;
  if (!current) {
    insertedAt = tr.doc.content.size;
    tr.insert(insertedAt, node);
  } else if (isBlankRow(current.node)) {
    insertedAt = current.start;
    tr.replaceWith(current.start, current.start + current.node.nodeSize, node);
  } else {
    insertedAt = current.start + current.node.nodeSize;
    tr.insert(insertedAt, node);
  }
  const after = insertedAt + node.nodeSize;

  const textblocks: { from: number; to: number }[] = [];
  tr.doc.nodesBetween(insertedAt, after, (child, pos) => {
    if (textblocks.length > 0) return false;
    if (!child.isTextblock) return true;
    textblocks.push({ from: pos + 1, to: pos + 1 + child.content.size });
    return false;
  });
  const [firstText] = textblocks;

  const next = tr.doc.nodeAt(after);
  const atEnd = after === tr.doc.content.size;
  if (atEnd || (!firstText && !(next && isBlankRow(next)))) {
    tr.insert(after, blankRow());
  }

  tr.setSelection(
    firstText
      ? TextSelection.create(tr.doc, firstText.from, firstText.to)
      : TextSelection.near(tr.doc.resolve(after + 1)),
  );
  editor.view.dispatch(tr.scrollIntoView());
  editor.view.focus();
}

/** Open the system file picker for images; empty if the user cancels. */
export function pickImageFiles({ multiple = false } = {}): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = multiple;
    input.hidden = true;
    // Attached rather than detached: some browsers won't open the picker, or
    // drop the change event, for an input that isn't in the document.
    document.body.appendChild(input);
    const done = (files: File[]) => {
      input.remove();
      resolve(files);
    };
    input.addEventListener(
      "change",
      () => done(Array.from(input.files ?? [])),
      {
        once: true,
      },
    );
    input.addEventListener("cancel", () => done([]), { once: true });
    input.click();
  });
}
